import React, { useState, useEffect } from 'react';
import Styled from 'styled';
import queryString from 'query-string';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Site } from 'four-nets/site';

import FilterView from 'four-nets/bazaar/filter/filter-view';
import FieldView from 'four-nets/bazaar/filter/field-view';
import Badge from 'four-nets/ui-v2/badges/badge/badge';
import { colorOrderingSk, colorOrderingCz } from 'four-nets/bazaar/bazaar-color-ordering';

import { PropertyFilterFragment } from './property-filter.graphql';
import { useHistory, useLocation } from 'react-router';

import defaultStyles from './property-filter.scss';

export interface Props extends PropertyFilterFragment {
    styles: typeof defaultStyles;
}

type Bucket = NonNullable<NonNullable<NonNullable<PropertyFilterFragment>['buckets']>[0]>;

const PropertyFilter: React.FC<Props> = (props, context) => {
    const { styles, id, propertyKey, name, buckets } = props;
    const site = context && context.site;

    const history = useHistory();
    const location = useLocation();

    const [fulltext, setFulltext] = useState('');
    const [active, setActive] = useState<Bucket | null>(null);
    const [page, setPage] = useState(queryString.parse(location.search).page || 1);

    const isMatchingSearch = (name?: string) => {
        if (!name) {
            return true;
        }
        return name.toLowerCase().indexOf(fulltext.toLowerCase()) !== -1;
    };

    let fields = buckets && buckets.filter(Boolean);

    const colorOrderingLocal = site && site.isSlovak ? colorOrderingSk : colorOrderingCz;

    if (propertyKey === 'color') {
        const orderMap = new Map();

        colorOrderingLocal.forEach((value, index) => {
            orderMap.set(value.name, index);
        });

        fields =
            fields &&
            fields.sort((b1, b2) => {
                return (orderMap.get(b1 && b1.bucketKey) || 0) - (orderMap.get(b2 && b2.bucketKey) || 0);
            });
    } else if (propertyKey === 'size') {
        fields =
            fields &&
            fields.sort((b1, b2) => {
                return parseInt((b1 && b1.bucketKey) || '0', 10) - parseInt((b2 && b2.bucketKey) || '0', 10);
            });
    }

    fields =
        fields &&
        (fields
            .filter(bucket => isMatchingSearch(bucket!.bucketKey!))
            .map(bucket =>
                bucket && bucket.background ? (
                    <button
                        onClick={() => {
                            setActive(bucket);
                        }}
                        key={bucket!.id}
                        className={classnames(styles.colorPropertyValue, {
                            [styles.selected]: Boolean(active && bucket!.id === active.id),
                        })}
                    >
                        <div className={styles.color} style={{ background: bucket!.background }} />
                        <div className={styles.colorLabel}>{bucket!.bucketKey}</div>
                        <Badge>{bucket!.docCount}</Badge>
                    </button>
                ) : (
                    <FieldView
                        key={bucket!.id}
                        pending={false}
                        name={bucket!.bucketKey || ''}
                        selected={Boolean(active && bucket!.id === active.id)}
                        count={bucket!.docCount}
                        onClick={() => {
                            setActive(bucket);
                        }}
                    />
                )
            ) as any);

    const confirm = () => {
        if (active) {
            history.push({
                pathname: location.pathname,
                search: `?${queryString.stringify({
                    ...queryString.parse(location.search),
                    [propertyKey!]: active.bucketKey,
                    page: 1,
                })}`,
            });
        } else {
            history.push({
                pathname: location.pathname,
                search: `?${queryString.stringify({
                    ...queryString.parse(location.search),
                    [propertyKey!]: undefined,
                    page,
                })}`,
            });
        }
    };

    const clear = () => {
        setPage(1);
        setActive(null);
        history.push({
            pathname: location.pathname,
            search: `?${queryString.stringify({
                ...queryString.parse(location.search),
                [propertyKey!]: undefined,
                page,
            })}`,
        });
    };

    useEffect(() => {
        const search = queryString.parse(location.search);
        if (buckets && search[propertyKey!]) {
            buckets.forEach(bucket => {
                if (bucket!.bucketKey === search[propertyKey!]) {
                    setActive(bucket);
                }
            });
        }
    }, [buckets, location.search, propertyKey]);

    return (
        <FilterView
            key={id || ''}
            title={name || ''}
            fields={fields as any}
            activeFilterFields={active ? active.bucketKey : undefined}
            fulltextValue={fulltext}
            onFulltextChange={e => setFulltext(e.target.value)}
            displayText={Boolean(fulltext && fields && fields.length === 0)}
            confirm={confirm}
            clear={clear}
        />
    );
};

PropertyFilter.contextTypes = {
    site: PropTypes.instanceOf(Site),
};

export default Styled(defaultStyles)(PropertyFilter);
